import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Card,
  CardBody,
  CardFooter,
  ButtonIcon,
  Container,
  Flex,
  Grid,
  Text,
  Alert,
  Link,
} from '@backstage/ui';
import { Progress } from '@backstage/core-components';
import { Page } from '@internal/backstage-plugin-hm-react';
import type { ScaffolderTaskStatus } from '@backstage/plugin-scaffolder-common';
import { TaskExplorerFilters } from './task-explorer-filters';
import { TaskExplorerTable } from './task-explorer-table';
import { useTaskExplorer, ScaffolderTask } from './hooks/use-task-explorer';
import { useSavedTasks } from './hooks/use-saved-tasks';
import { STATUS_COLORS, formatDate } from './task-explorer-utils';
import { RiCloseLine, RiEraserFill, RiPushpin2Fill } from '@remixicon/react';

const WELCOME_ALERT_CLOSED_STORAGE_KEY =
  'backstage:task-explorer:welcome-alert-closed';

const loadIsWelcomeAlertClosed = (): boolean => {
  if (typeof localStorage === 'undefined') {
    return false;
  }

  try {
    return localStorage.getItem(WELCOME_ALERT_CLOSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const persistIsWelcomeAlertClosed = (isClosed: boolean): void => {
  if (typeof localStorage === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(WELCOME_ALERT_CLOSED_STORAGE_KEY, String(isClosed));
  } catch {
    // Ignore storage write errors and keep UI responsive.
  }
};

const MAX_ACCUMULATED_OPTIONS = 500;

export const TaskExplorerPage = () => {
  const [createdBy, setCreatedBy] = useState('');
  const [templateFilter, setTemplateFilter] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<
    ScaffolderTaskStatus[]
  >([]);
  const [dateRange, setDateRange] = useState('30d');
  const [isWelcomeAlertClosed, setIsWelcomeAlertClosed] = useState(
    loadIsWelcomeAlertClosed,
  );

  const { tasks, isLoading, error } = useTaskExplorer({
    createdBy: createdBy || undefined,
    templateFilter: templateFilter.length > 0 ? templateFilter : undefined,
    status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
    dateRange,
  });

  const { savedTasks, isSaved, saveTask, removeTask, clearAll } =
    useSavedTasks();

  const templateMapRef = useRef(new Map<string, string>());

  const templateOptions = useMemo(() => {
    for (const task of tasks) {
      if (templateMapRef.current.size >= MAX_ACCUMULATED_OPTIONS) break;
      const info = task.spec?.templateInfo;
      const entityRef = info?.entityRef;
      if (entityRef && !templateMapRef.current.has(entityRef)) {
        const title =
          info?.entity?.metadata?.title ??
          entityRef.split('/').at(-1) ??
          entityRef;
        templateMapRef.current.set(entityRef, title);
      }
    }
    return [...templateMapRef.current.entries()]
      .map(([entityRef, title]) => ({ label: title, value: entityRef }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [tasks]);

  const userSetRef = useRef(new Set<string>());

  const userOptions = useMemo(() => {
    for (const task of tasks) {
      if (userSetRef.current.size >= MAX_ACCUMULATED_OPTIONS) break;
      if (task.createdBy) userSetRef.current.add(task.createdBy);
    }
    return [...userSetRef.current].sort((left, right) =>
      left.localeCompare(right),
    );
  }, [tasks]);

  const handleToggleSave = (task: ScaffolderTask) => {
    if (isSaved(task.id)) {
      removeTask(task.id);
    } else {
      saveTask({
        id: task.id,
        templateRef: task.spec?.templateInfo?.entityRef ?? 'unknown',
        templateTitle:
          task.spec?.templateInfo?.entity?.metadata?.title ??
          task.spec?.templateInfo?.entityRef?.split('/').at(-1) ??
          'Unknown',
        createdBy: task.createdBy ?? 'unknown',
        status: task.status,
        createdAt: task.createdAt,
      });
    }
  };

  const handleCreatedByChange = useCallback((value: string) => {
    setCreatedBy(value);
  }, []);

  const handleStatusChange = useCallback((statuses: ScaffolderTaskStatus[]) => {
    setSelectedStatuses(statuses);
  }, []);

  const handleTemplateFilterChange = useCallback((value: string[]) => {
    setTemplateFilter(value);
  }, []);

  const handleDateRangeChange = useCallback((value: string) => {
    setDateRange(value);
  }, []);

  const handleCloseWelcomeAlert = useCallback(() => {
    setIsWelcomeAlertClosed(true);
    persistIsWelcomeAlertClosed(true);
  }, []);

  return (
    <Page title="Task Explorer">
      <Container>
        <Flex direction="column" gap="8">
          {!isWelcomeAlertClosed && (
            <Alert
              status="info"
              title="Welcome to the Task Explorer!"
              description={
                <Text>
                  The Task Explorer is a tool for browsing template task runs
                  across all users and templates. It provides powerful filters
                  to help you debug template issues or track specific runs over
                  time. Please give it a try and{' '}
                  <Link
                    href="https://hm.ly/backstage-teams"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    share your feedback
                  </Link>{' '}
                  with the Backstage team!
                </Text>
              }
              customActions={
                <ButtonIcon
                  size="small"
                  variant="tertiary"
                  icon={<RiCloseLine />}
                  aria-label="Close"
                  onPress={handleCloseWelcomeAlert}
                />
              }
            />
          )}

          {savedTasks.length > 0 && (
            <Flex direction="column" gap="2">
              <Flex direction="row" gap="2" align="center">
                <Text variant="title-x-small">
                  Saved Tasks ({savedTasks.length})
                </Text>
                <ButtonIcon
                  variant="secondary"
                  aria-label="Clear all saved tasks"
                  onPress={clearAll}
                  icon={<RiEraserFill />}
                />
              </Flex>
              <Grid.Root columns="4" gap="4">
                {savedTasks.map(task => (
                  <Grid.Item key={task.id}>
                    <Card
                      href={`/create/tasks/${task.id}`}
                      label={`View task ${task.templateTitle}`}
                    >
                      <CardBody>
                        <Flex direction="column" gap="1">
                          <Text variant="body-medium">
                            {task.templateTitle}
                          </Text>
                          <Text variant="body-small" color="secondary">
                            {task.createdBy.split('/').pop()}
                          </Text>
                          <Flex direction="row" gap="2" align="center">
                            <Text
                              variant="body-small"
                              weight="bold"
                              color={STATUS_COLORS[task.status] ?? 'secondary'}
                            >
                              {task.status}
                            </Text>
                            <Text variant="body-small" color="secondary">
                              {task.createdAt
                                ? formatDate(task.createdAt)
                                : '—'}
                            </Text>
                          </Flex>
                        </Flex>
                      </CardBody>
                      <CardFooter>
                        <ButtonIcon
                          variant="secondary"
                          aria-label="Remove saved task"
                          onPress={() => removeTask(task.id)}
                          icon={<RiPushpin2Fill />}
                        />
                      </CardFooter>
                    </Card>
                  </Grid.Item>
                ))}
              </Grid.Root>
            </Flex>
          )}

          <TaskExplorerFilters
            onCreatedByChange={handleCreatedByChange}
            onTemplateFilterChange={handleTemplateFilterChange}
            onStatusChange={handleStatusChange}
            onDateRangeChange={handleDateRangeChange}
            selectedStatuses={selectedStatuses}
            createdBy={createdBy}
            dateRange={dateRange}
            templateFilter={templateFilter}
            templateOptions={templateOptions}
            userOptions={userOptions}
          />

          {isLoading && <Progress />}

          {error && (
            <Text color="warning">Failed to load tasks: {error.message}</Text>
          )}

          {!isLoading && !error && (
            <Flex direction="column" gap="4">
              <TaskExplorerTable
                tasks={tasks}
                isSaved={isSaved}
                onToggleSave={handleToggleSave}
              />
            </Flex>
          )}
        </Flex>
      </Container>
    </Page>
  );
};
